import React, { useEffect, useState, type ReactElement, type ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
  toast,
} from "@ndd/ui";
import {
  BLOCK_TIME_DESCRIPTION,
  blockTimeErrors,
  blockTimeZoneLabel,
  defaultBlockTimeForm,
  type BlockTimeField,
  type BlockTimeFieldError,
  type BlockTimeForm,
} from "../../lib/rd-block-time.js";
import { failureOf, useCreateRdTimeBlock } from "../../lib/use-create-rd-time-block.js";
import { useRdBlockTimeZone } from "../../lib/use-rd-block-time-zone.js";
import { BLOCK_TIME_FIELD_ID, BlockTimeFormBody } from "./block-time-form.js";

// NO-877: the Block time dialog behind the Week view's header button. On
// opening it asks the blocks API which zone the times are read in, then shows
// the form. It checks the form before it saves, saves one block, and on
// success closes; the Week refreshes through the mutation. A refusal stays in
// the dialog with the RD's entries intact.

interface BlockTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The RD a supervisor is viewing; null saves to the signed-in RD. */
  rdId: string | null;
}

function BlockTimeDialog({
  open,
  onOpenChange,
  rdId,
}: BlockTimeDialogProps): ReactElement {
  // A block being saved is not interrupted by closing, as in the appointment dialogs.
  const [saving, setSaving] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saving) return;

        onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          "@container rounded-[22px] sm:max-w-[560px]",
          "[&>button:last-child]:grid [&>button:last-child]:size-8 [&>button:last-child]:place-items-center [&>button:last-child]:rounded-[10px] [&>button:last-child]:text-muted-strong [&>button:last-child]:opacity-100 [&>button:last-child]:hover:bg-accent [&>button:last-child>svg]:size-[18px]"
        )}
      >
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-heading-sm font-bold leading-title">
            Block time
          </DialogTitle>
          <DialogDescription>{BLOCK_TIME_DESCRIPTION}</DialogDescription>
        </DialogHeader>
        {open ? (
          <BlockTimeZoneGate
            rdId={rdId}
            onClose={() => onOpenChange(false)}
            onSaving={setSaving}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Mounted per opening: reads the zone, then hands a fresh form its first values. */
function BlockTimeZoneGate({
  rdId,
  onClose,
  onSaving,
}: {
  rdId: string | null;
  onClose: () => void;
  onSaving: (saving: boolean) => void;
}): ReactElement {
  const zone = useRdBlockTimeZone(rdId);

  if (zone.status === "ready") {
    return (
      <BlockTimeSession
        rdId={rdId}
        timeZone={zone.timeZone}
        onClose={onClose}
        onSaving={onSaving}
      />
    );
  }

  if (zone.status === "loading") {
    return (
      <p role="status" className="m-0 text-body text-muted-strong">
        Checking your calendar…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p role="alert" className="m-0 text-body text-destructive">
        {
          "We couldn’t check which time zone your calendar uses, so nothing was blocked. Try again in a moment."
        }
      </p>
      <div className="flex items-center justify-end gap-2.5">
        <Button type="button" variant="utility" size="compact" onClick={onClose}>
          Close
        </Button>
        <Button
          type="button"
          variant="press"
          size="compact"
          shape="pill"
          onClick={zone.retry}
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

/** Tells the dialog whether a save is running, and that none is once the form goes. */
function useReportSaving(onSaving: (saving: boolean) => void, saving: boolean): void {
  useEffect(() => {
    onSaving(saving);

    return () => onSaving(false);
  }, [onSaving, saving]);
}

/** The form locks while a save runs, so focus waits for the render that unlocks it. */
function useFocusWhenUnlocked(
  target: BlockTimeField | null,
  locked: boolean,
  setTarget: (target: BlockTimeField | null) => void
): void {
  useEffect(() => {
    if (target === null || locked) return;

    focusField(target);
    setTarget(null);
  }, [target, locked, setTarget]);
}

function BlockTimeSession({
  rdId,
  timeZone,
  onClose,
  onSaving,
}: {
  rdId: string | null;
  timeZone: string;
  onClose: () => void;
  onSaving: (saving: boolean) => void;
}): ReactElement {
  const [opened] = useState(() => new Date().toISOString());
  const [form, setForm] = useState<BlockTimeForm>(() =>
    defaultBlockTimeForm(opened, timeZone)
  );
  const [errors, setErrors] = useState<readonly BlockTimeFieldError[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [focusTarget, setFocusTarget] = useState<BlockTimeField | null>(null);
  const save = useCreateRdTimeBlock(rdId);

  useReportSaving(onSaving, save.isPending);

  useFocusWhenUnlocked(focusTarget, save.isPending, setFocusTarget);

  const change = (next: BlockTimeForm): void => {
    setForm(next);
    setErrors([]);
    setFailure(null);
  };

  const submit = (): void => {
    const found = blockTimeErrors(form);
    setErrors(found);
    setFailure(null);

    if (found.length > 0) {
      setFocusTarget(found[0]!.field);

      return;
    }

    save.mutate(form, {
      onSuccess: () => {
        toast.success("Time blocked");
        onClose();
      },
      onError: (error) => {
        const refused = failureOf(error);
        setFailure(refused.message);
        setFocusTarget(refused.field);
      },
    });
  };

  return (
    <BlockTimeFormBody
      form={form}
      onChange={change}
      errors={errors}
      zoneLabel={blockTimeZoneLabel(timeZone, opened)}
      failure={failure}
      saving={save.isPending}
      onSubmit={submit}
      onClose={onClose}
    />
  );
}

function focusField(field: BlockTimeField): void {
  const target =
    field === "kind"
      ? document.querySelector<HTMLElement>('input[name="block-time-kind"]:checked')
      : document.getElementById(BLOCK_TIME_FIELD_ID[field]);

  target?.focus();
}

/**
 * The header button's handler and the dialog it opens. The page mounts the
 * dialog (NO-912), so the button works from every tab. With `enabled` false
 * (the `rd-schedule` gate is closed) there is no handler.
 */
export function useBlockTimeAction(
  rdId: string | null,
  enabled: boolean
): {
  onBlockTime: (() => void) | undefined;
  dialog: ReactNode;
} {
  const [open, setOpen] = useState(false);

  if (!enabled) return { onBlockTime: undefined, dialog: null };

  return {
    onBlockTime: () => setOpen(true),
    dialog: <BlockTimeDialog open={open} onOpenChange={setOpen} rdId={rdId} />,
  };
}
